/**
 * Registration Context
 *
 * Manages the registration session state across the multi-step registration flow.
 * Stores user ID, registration token, and uploaded documents.
 *
 * Session state lives in a module-level store so NativeRegistration,
 * ApplicationStatus, and DocumentUpload can each wrap with RegistrationProvider
 * and still share the same session across navigation.
 *
 * On web, state is persisted to sessionStorage so a refresh in the same tab
 * keeps the session, and closing the tab destroys it. Native keeps the
 * session in memory only. Passwords are never written to storage.
 *
 * Usage:
 * 1. Wrap registration screens with withRegistrationProvider() or RegistrationProvider
 * 2. Use useRegistrationContext() hook to access/update session state
 *
 * Flow:
 * - Step 1: Create user -> Capture userId & registrationToken
 * - Step 2: Update registration -> Store company details
 * - Step 3: Upload documents -> Store document URLs
 */

import React, {
  ComponentType,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  useEffect,
  ReactNode,
} from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  UploadedDocument,
  DocumentType,
} from "@scenes/authentication/NativeRegistration/services/documentUploadApi";
import { UserActivationData } from "@src/types/UserActivation";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";

const STORAGE_KEY = "registration_session_state";

// ============== Types ==============

export interface Step1Data {
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  password: string;
  password_confirmation: string;
  is_newsletter_check?: string;
}

export interface Step2Data {
  title?: string;
  website?: string;
  business_type?: string[];
  phone_number?: string;
  phone_number_country_code?: string;
  phone_number_type?: string;
  mobile_phone?: string;
  mobile_phone_country_code?: string;
  mobile_phone_type?: string;
  street_address?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  heard_about_us?: string;
  is_list_check?: boolean;
}

export interface UploadedDocuments {
  companyProof: UploadedDocument[];
  identityVerification: UploadedDocument[];
}

export interface RegistrationSessionState {
  userId: number | null;
  registrationToken: string | null;
  email: string | null;
  companyName: string | null;
  location: string | null;
  currentStep: 1 | 2 | 3;
  step1Data: Step1Data | null;
  step2Data: Step2Data | null;
  uploadedDocuments: UploadedDocuments;
  documentUploadMandatory: boolean | null;
  isLoading: boolean;
  error: string | null;
  activationData: UserActivationData | null;
}

export interface RegistrationContextValue extends RegistrationSessionState {
  setStep1Complete: (
    userId: number,
    registrationToken: string,
    data: Step1Data
  ) => void;
  setStep2Complete: (data: Step2Data) => void;
  addUploadedDocument: (
    documentType: DocumentType,
    document: UploadedDocument
  ) => void;
  removeUploadedDocument: (
    documentType: DocumentType,
    documentId: string
  ) => void;
  getDocumentUrls: () => {
    companyProof: string[];
    identityVerification: string[];
  };
  setDocumentUploadMandatory: (mandatory: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetSession: () => void;
  canProceedToStep2: () => boolean;
  canProceedToStep3: () => boolean;
  canSubmitDocuments: () => boolean;
  setActivationData: (data: UserActivationData) => void;
  isSessionHydrated: boolean;
}

// ============== Initial State ==============

const initialState: RegistrationSessionState = {
  userId: null,
  registrationToken: null,
  email: null,
  companyName: null,
  location: null,
  currentStep: 1,
  step1Data: null,
  step2Data: null,
  uploadedDocuments: {
    companyProof: [],
    identityVerification: [],
  },
  documentUploadMandatory: null,
  isLoading: false,
  error: null,
  activationData: null,
};

// ============== Shared Session Store ==============
// Module-level store so providers on Register / ApplicationStatus / DocumentUpload
// share one session across screen navigations.
// Web persists to sessionStorage (cleared when the tab closes). Native is memory-only.

type SessionListener = () => void;

let sessionState: RegistrationSessionState = initialState;
let isHydrated = false;
const sessionListeners = new Set<SessionListener>();
const hydrationListeners = new Set<SessionListener>();

const getHydrationSnapshot = () => isHydrated;

const subscribeToHydration = (listener: SessionListener) => {
  hydrationListeners.add(listener);
  return () => {
    hydrationListeners.delete(listener);
  };
};

const markSessionHydrated = () => {
  if (isHydrated) return;
  isHydrated = true;
  hydrationListeners.forEach((listener) => listener());
};

export const navigateToLoginScreen = (theme: {
  isLargeScreen?: boolean;
  isTablet?: boolean;
}) => {
  navigate(
    theme.isLargeScreen || theme.isTablet
      ? Routes.OnboardingWeb
      : Routes.Authentication
  );
};

const getSessionSnapshot = () => sessionState;

const subscribeToSession = (listener: SessionListener) => {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
};

const getWebSessionStorage = (): Storage | null => {
  if (Platform.OS !== "web") return null;

  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch {
    // sessionStorage not available
  }

  return null;
};

/** Storage must never contain credentials, even for the current tab. */
const withoutPasswords = (
  state: RegistrationSessionState
): RegistrationSessionState => {
  if (!state.step1Data) return state;

  return {
    ...state,
    step1Data: {
      ...state.step1Data,
      password: "",
      password_confirmation: "",
    },
  };
};

const persistSessionState = (state: RegistrationSessionState) => {
  const storage = getWebSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(withoutPasswords(state)));
  } catch (error) {
    console.warn("Failed to persist registration session state:", error);
  }
};

const removeLegacyAsyncStorage = async () => {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear legacy registration session state:", error);
  }
};

const setSessionState = (
  updater:
    | RegistrationSessionState
    | ((prev: RegistrationSessionState) => RegistrationSessionState)
) => {
  sessionState =
    typeof updater === "function" ? updater(sessionState) : updater;
  sessionListeners.forEach((listener) => listener());
  persistSessionState(sessionState);
};

/**
 * Rehydrates session state from sessionStorage on web.
 * Drops any copy previously written to AsyncStorage/localStorage.
 * Called once on app initialization.
 */
export const rehydrateSessionState = async (): Promise<void> => {
  if (isHydrated) return;

  const hadInMemorySession = sessionState !== initialState;
  const storage = getWebSessionStorage();

  if (storage && !hadInMemorySession) {
    try {
      const stored = storage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as RegistrationSessionState;
        sessionState = withoutPasswords({
          ...initialState,
          ...parsed,
          uploadedDocuments: {
            ...initialState.uploadedDocuments,
            ...(parsed.uploadedDocuments || {}),
          },
        });
        sessionListeners.forEach((listener) => listener());
      }
    } catch (error) {
      console.warn("Failed to rehydrate registration session state:", error);
    }
  }

  markSessionHydrated();
  await removeLegacyAsyncStorage();
};

/**
 * Clears persisted session state from storage.
 * Call this on logout or when registration is complete.
 */
export const clearPersistedSessionState = async (): Promise<void> => {
  try {
    getWebSessionStorage()?.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear registration session state:", error);
  }

  await removeLegacyAsyncStorage();
};

/**
 * Sets activation data on the shared session without a mounted provider.
 * Useful from login redirects before registration screens mount.
 */
export const setSharedActivationData = (data: UserActivationData) => {
  setSessionState((prev) => ({
    ...prev,
    activationData: data,
    email: data.email_address ?? prev.email,
    userId: data.user_id ?? prev.userId,
    registrationToken:
      data.registration_token ??
      data.registrationToken ??
      prev.registrationToken,
    companyName: data.company_name ?? data.companyName ?? prev.companyName,
    location: data.location ?? data.country ?? prev.location,
  }));
};

// ============== Context ==============

const RegistrationContext = createContext<RegistrationContextValue | null>(
  null
);

// ============== Provider ==============

interface RegistrationProviderProps {
  children: ReactNode;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({
  children,
}) => {
  const state = useSyncExternalStore(
    subscribeToSession,
    getSessionSnapshot,
    getSessionSnapshot
  );
  const isSessionHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydrationSnapshot,
    () => false
  );

  useEffect(() => {
    rehydrateSessionState();
  }, []);

  /**
   * Called after Step 1 (Create User) completes successfully
   * Stores userId, registrationToken, and step 1 form data
   */
  const setStep1Complete = useCallback(
    (userId: number, registrationToken: string, data: Step1Data) => {
      setSessionState((prev) => ({
        ...prev,
        userId,
        registrationToken,
        email: data.email,
        companyName: data.company_name,
        currentStep: 2,
        step1Data: data,
        error: null,
      }));
    },
    []
  );

  /**
   * Called after Step 2 (Update Registration) completes successfully
   * Stores step 2 form data and updates location
   */
  const setStep2Complete = useCallback((data: Step2Data) => {
    setSessionState((prev) => ({
      ...prev,
      currentStep: 3,
      step2Data: data,
      location: data.city
        ? `${data.city}${data.state ? `, ${data.state}` : ""}${
            data.country ? `, ${data.country}` : ""
          }`
        : data.country || null,
      error: null,
    }));
  }, []);

  /**
   * Adds an uploaded document to the session
   */
  const addUploadedDocument = useCallback(
    (documentType: DocumentType, document: UploadedDocument) => {
      setSessionState((prev) => {
        const key =
          documentType === "company_proof"
            ? "companyProof"
            : "identityVerification";

        return {
          ...prev,
          uploadedDocuments: {
            ...prev.uploadedDocuments,
            [key]: [...prev.uploadedDocuments[key], document],
          },
        };
      });
    },
    []
  );

  /**
   * Removes an uploaded document from the session
   */
  const removeUploadedDocument = useCallback(
    (documentType: DocumentType, documentId: string) => {
      setSessionState((prev) => {
        const key =
          documentType === "company_proof"
            ? "companyProof"
            : "identityVerification";

        return {
          ...prev,
          uploadedDocuments: {
            ...prev.uploadedDocuments,
            [key]: prev.uploadedDocuments[key].filter(
              (doc) => doc.id !== documentId
            ),
          },
        };
      });
    },
    []
  );

  /**
   * Gets document URLs for Step 3 submission
   */
  const getDocumentUrls = useCallback(() => {
    return {
      companyProof: state.uploadedDocuments.companyProof.map((doc) => doc.url),
      identityVerification: state.uploadedDocuments.identityVerification.map(
        (doc) => doc.url
      ),
    };
  }, [state.uploadedDocuments]);

  /**
   * Sets whether document upload is mandatory
   */
  const setDocumentUploadMandatory = useCallback((mandatory: boolean) => {
    setSessionState((prev) => ({
      ...prev,
      documentUploadMandatory: mandatory,
    }));
  }, []);

  /**
   * Sets loading state
   */
  const setLoading = useCallback((loading: boolean) => {
    setSessionState((prev) => ({
      ...prev,
      isLoading: loading,
    }));
  }, []);

  /**
   * Sets error state
   */
  const setError = useCallback((error: string | null) => {
    setSessionState((prev) => ({
      ...prev,
      error,
    }));
  }, []);

  /**
   * Resets the entire registration session and clears persisted state
   */
  const resetSession = useCallback(() => {
    setSessionState(initialState);
    clearPersistedSessionState();
  }, []);

  /**
   * Checks if user can proceed to Step 2
   */
  const canProceedToStep2 = useCallback(() => {
    return state.userId !== null && state.registrationToken !== null;
  }, [state.userId, state.registrationToken]);

  /**
   * Checks if user can proceed to Step 3 (document upload)
   */
  const canProceedToStep3 = useCallback(() => {
    return canProceedToStep2() && state.step2Data !== null;
  }, [canProceedToStep2, state.step2Data]);

  /**
   * Checks if user can submit documents
   * Both company proof and identity verification must have at least one document
   */
  const canSubmitDocuments = useCallback(() => {
    return (
      state.uploadedDocuments.companyProof.length > 0 &&
      state.uploadedDocuments.identityVerification.length > 0
    );
  }, [state.uploadedDocuments]);

  /**
   * Stores activation data for ApplicationStatus / DocumentUpload navigation
   */
  const setActivationData = useCallback((data: UserActivationData) => {
    setSharedActivationData(data);
  }, []);

  const contextValue = useMemo<RegistrationContextValue>(
    () => ({
      ...state,
      setStep1Complete,
      setStep2Complete,
      addUploadedDocument,
      removeUploadedDocument,
      getDocumentUrls,
      setDocumentUploadMandatory,
      setLoading,
      setError,
      resetSession,
      canProceedToStep2,
      canProceedToStep3,
      canSubmitDocuments,
      setActivationData,
      isSessionHydrated,
    }),
    [
      state,
      isSessionHydrated,
      setStep1Complete,
      setStep2Complete,
      addUploadedDocument,
      removeUploadedDocument,
      getDocumentUrls,
      setDocumentUploadMandatory,
      setLoading,
      setError,
      resetSession,
      canProceedToStep2,
      canProceedToStep3,
      canSubmitDocuments,
      setActivationData,
    ]
  );

  return (
    <RegistrationContext.Provider value={contextValue}>
      {children}
    </RegistrationContext.Provider>
  );
};

// ============== HOC ==============

/**
 * Wraps a screen with RegistrationProvider when one is not already present.
 * Use on NativeRegistration, ApplicationStatus, and DocumentUpload.
 */
export function withRegistrationProvider<P extends object>(
  Component: ComponentType<P>
): ComponentType<P> {
  const Wrapped: React.FC<P> = (props) => {
    const existingContext = useRegistrationContextSafe();

    if (existingContext) {
      return <Component {...props} />;
    }

    return (
      <RegistrationProvider>
        <Component {...props} />
      </RegistrationProvider>
    );
  };

  Wrapped.displayName = `withRegistrationProvider(${
    Component.displayName || Component.name || "Component"
  })`;

  return Wrapped;
}

// ============== Hook ==============

/**
 * Hook to access registration context
 * Must be used within a RegistrationProvider
 */
export const useRegistrationContext = (): RegistrationContextValue => {
  const context = useContext(RegistrationContext);

  if (!context) {
    throw new Error(
      "useRegistrationContext must be used within a RegistrationProvider"
    );
  }

  return context;
};

/**
 * Hook to check if registration context is available
 * Useful for optional context usage
 */
export const useRegistrationContextSafe =
  (): RegistrationContextValue | null => {
    return useContext(RegistrationContext);
  };

export default RegistrationContext;
